import React from 'react';
import { useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';

import { i18n } from '../tasksReportMacroI18n.ts';
import type { TasksReportStatus } from '../tasksReportStatus.ts';

interface EmptyStateProps {
	status: TasksReportStatus | undefined;
	/** True if neither spaces nor pages were selected (vs. showing task-status-based empty states) */
	noSpacesSelected?: boolean;
}

const styles = cssMap({
	container: {
		padding: token('space.300'),
		textAlign: 'center',
	},
	title: {
		font: token('font.heading.xxsmall'),
		fontWeight: token('font.weight.semibold'),
	},
	description: {
		color: token('color.text.subtlest'),
		font: token('font.body'),
	},
});

export const EmptyState = ({ status, noSpacesSelected }: EmptyStateProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	if (noSpacesSelected) {
		return (
			<Box xcss={styles.container} testId="tasks-report-empty-state-no-spaces">
				<Stack space="space.100" alignInline="center">
					<Box xcss={styles.title}>{formatMessage(i18n.noSpacesSelectedTitle)}</Box>
					<Box xcss={styles.description}>{formatMessage(i18n.noSpacesSelectedDescription)}</Box>
				</Stack>
			</Box>
		);
	}

	const title =
		status === 'complete'
			? formatMessage(i18n.noCompleteTasksTitle)
			: formatMessage(i18n.noIncompleteTasksTitle);

	return (
		<Box xcss={styles.container} testId="tasks-report-empty-state">
			<Stack space="space.100" alignInline="center">
				<Box xcss={styles.title}>{title}</Box>
				<Box xcss={styles.description}>{formatMessage(i18n.emptyDescription)}</Box>
			</Stack>
		</Box>
	);
};
